import { useEffect, useRef, useState } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import Landing from "@/pages/Landing";
import Login from "@/pages/Login";
import AuthCallback from "@/pages/AuthCallback";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import VerifyEmail from "@/pages/VerifyEmail";
import Onboarding from "@/pages/Onboarding";
import OwnerDashboard from "@/pages/OwnerDashboard";
import ShopDashboard from "@/pages/ShopDashboard";
import AdminDashboard from "@/pages/AdminDashboard";
import AdminLogin from "@/pages/AdminLogin";
import { AdminAuthGuard } from "@/components/AdminAuthGuard";
import Settings from "@/pages/Settings";
import PendingApproval from "@/pages/PendingApproval";
import ShopApplication from "@/pages/ShopApplication";
import { ImpersonationBanner } from "@/components/ImpersonationBanner";
import { TrialEndingToast } from "@/components/TrialEndingToast";
import { InstallPrompt } from "@/components/InstallPrompt";
import { useFcmPush } from "@/hooks/useFcmPush";
import PostIssue from "@/pages/PostIssue";
import IssueDetail from "@/pages/IssueDetail";
import ShopsList from "@/pages/ShopsList";
import Messages from "@/pages/Messages";
import ShopProfile from "@/pages/ShopProfile";
import Upgrade from "@/pages/Upgrade";
import Privacy from "@/pages/Privacy";
import DeleteAccount from "@/pages/DeleteAccount";
import Terms from "@/pages/Terms";
import MapView from "@/pages/MapView";
import Welcome from "@/pages/Welcome";
import Mileage from "@/pages/Mileage";
import Referrals from "@/pages/Referrals";
import W9Application from "@/pages/W9Application";
import AdminReferrals from "@/pages/AdminReferrals";
import Support from "@/pages/Support";
import Maintenance from "@/pages/Maintenance";
import OwnerUpgrade from "@/pages/OwnerUpgrade";
import Receipts from "@/pages/Receipts";
import { DeepLinkListener } from "@/components/DeepLinkListener";
import { initNativeShell } from "@/lib/native";
import { initIap } from "@/lib/iap";
import { initRevenueCat, logOutRevenueCat } from "@/services/revenuecat";
import { Capacitor } from "@capacitor/core";
import { api } from "@/lib/api";
import { useFeatureFlags } from "@/hooks/useFeatureFlags";

function Protected({ children, role }) {
  const { user, loading } = useAuth();
  if (loading) {
    return <div className="min-h-screen bg-[#09090B] flex items-center justify-center text-zinc-500 label-eyebrow">LOADING...</div>;
  }
  if (!user) return <Navigate to="/login" replace />;
  if (!user.role) return <Navigate to="/onboarding" replace />;
  // Only rejected / suspended shops are hard-blocked from the dashboard.
  // Pending shops now get full dashboard access (Account First, Application Second).
  // shop-status-access-nov2: suspended shops keep only the notice + account
  // page (billing / delete); rejected shops can also reapply.
  if (user.role === "shop" && (user.status === "rejected" || user.status === "suspended" || user.status === "deactivated")) {
    const allowed = user.status === "rejected" ? ["/pending", "/settings", "/shop/apply"] : ["/pending", "/settings"];
    if (!allowed.includes(window.location.pathname)) {
      return <Navigate to="/pending" replace />;
    }
  }
  if (role && user.role !== role) {
    const dest = user.role === "admin" ? "/admin" : user.role === "shop" ? "/shop" : "/owner";
    return <Navigate to={dest} replace />;
  }
  return children;
}


/** Referral / Ambassador / W-9 pages exist only while the backend
 *  REFERRALS_ENABLED flag is on (served via GET /api/config). While the flag
 *  is loading we show the loader; when it is off we redirect to "/" (which
 *  AutoRoute sends to the user's dashboard). Fails closed. */
function ReferralsRoute({ children }) {
  const { referralsEnabled, loaded } = useFeatureFlags();
  if (!loaded) {
    return <div className="min-h-screen bg-[#09090B] flex items-center justify-center text-zinc-500 label-eyebrow">LOADING...</div>;
  }
  if (!referralsEnabled) return <Navigate to="/" replace />;
  return children;
}

/** Auto-routes authenticated users to their dashboard on app launch.
 *  Prevents the Landing page from showing when a token is already stored. */
function AutoRoute() {
  const { user, loading } = useAuth();
  if (loading) {
    return <div className="min-h-screen bg-[#09090B] flex items-center justify-center text-zinc-500 label-eyebrow">LOADING...</div>;
  }
  if (user) {
    const dest = user.role === "admin" ? "/admin" : user.role === "shop" ? "/shop" : "/owner";
    return <Navigate to={dest} replace />;
  }
  return <Landing />;
}
function RootRouter() {
  const location = useLocation();
  // Handle OAuth callback via URL fragment (session_id=...)
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  // First-launch walkthrough - redirect root to /welcome until user dismisses it.
  // Skip for OAuth callback paths, deep links with ?ref= referral codes
  // (we still want to show welcome but capture ref first), or when explicitly visiting /welcome.
  let showWelcome = false;
  try {
    showWelcome =
      location.pathname === "/" &&
      !localStorage.getItem("hasSeenOnboarding_v1") &&
      !location.search?.includes("ref=");
  } catch { /* localStorage unavailable in some embeds */ }
  if (showWelcome) {
    return <Navigate to="/welcome" replace />;
  }
  return (
    <Routes>
      <Route path="/" element={<AutoRoute />} />
      <Route path="/welcome" element={<Welcome />} />
      <Route path="/login" element={<Login />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/verify-email" element={<VerifyEmail />} />
      <Route path="/auth/callback" element={<AuthCallback />} />
      <Route path="/onboarding" element={<Onboarding />} />
      <Route path="/owner" element={<Protected role="owner"><OwnerDashboard /></Protected>} />
      <Route path="/post" element={<Protected role="owner"><PostIssue /></Protected>} />
      <Route path="/shop" element={<Protected role="shop"><ShopDashboard /></Protected>} />
      <Route path="/browse" element={<Protected role="shop"><ShopDashboard /></Protected>} />
      <Route path="/shop-profile" element={<Protected role="shop"><ShopProfile /></Protected>} />
      <Route path="/admin/login" element={<AdminLogin />} />
      <Route path="/admin" element={<AdminAuthGuard><AdminDashboard /></AdminAuthGuard>} />
      <Route path="/admin/referrals" element={<AdminAuthGuard><AdminReferrals /></AdminAuthGuard>} />
      <Route path="/pending" element={<Protected><PendingApproval /></Protected>} />
      <Route path="/shop/apply" element={<Protected><ShopApplication /></Protected>} />
      <Route path="/shops" element={<Protected><ShopsList /></Protected>} />
      <Route path="/issue/:id" element={<Protected><IssueDetail /></Protected>} />
      <Route path="/messages" element={<Protected><Messages /></Protected>} />
      <Route path="/settings" element={<Protected><Settings /></Protected>} />
      <Route path="/upgrade" element={<Protected role="shop"><Upgrade /></Protected>} />
      <Route path="/map" element={<Protected><MapView /></Protected>} />
      <Route path="/mileage" element={<Protected role="owner"><Mileage /></Protected>} />
      <Route path="/owner/upgrade" element={<Protected role="owner"><OwnerUpgrade /></Protected>} />
      <Route path="/owner/receipts" element={<Protected role="owner"><Receipts /></Protected>} />
      <Route path="/owner/w9-application" element={<ReferralsRoute><Protected role="owner"><W9Application /></Protected></ReferralsRoute>} />
      <Route path="/owner/profit-radar" element={<Navigate to="/mileage" state={{ tab: "profit_radar" }} replace />} />
      <Route path="/referrals" element={<ReferralsRoute><Protected><Referrals /></Protected></ReferralsRoute>} />
      <Route path="/privacy" element={<Privacy />} />
      {/* Public (no login): Google Play "delete account" web link */}
      <Route path="/delete-account" element={<DeleteAccount />} />
      <Route path="/terms" element={<Terms />} />
      <Route path="/support" element={<Support />} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function AppContent() {
  const { user } = useAuth();
  const [maintenanceMode, setMaintenanceMode] = useState(false);
  const [maintenanceMsg, setMaintenanceMsg] = useState("");

  // Check kill switch status on mount and periodically
  useEffect(() => {
    let active = true;
    const check = async () => {
      try {
        const res = await fetch("/api/killswitch/status");
        const data = await res.json();
        if (active) {
          setMaintenanceMode(data.maintenance || false);
          setMaintenanceMsg(data.message || "");
        }
      } catch { /* ignore */ }
    };
    check();
    const id = setInterval(check, 30000);
    return () => { active = false; clearInterval(id); };
  }, []);

  // Show maintenance screen for non-admin users
  if (maintenanceMode && (!user || user.role !== "admin")) {
    return <Maintenance message={maintenanceMsg} />;
  }

  // Only add the sidebar gap if a user is logged in
  const paddingClass = user ? "lg:pl-64" : "";

  return (
    <div className={`App ${paddingClass}`}>
      <Toaster
        theme="dark"
        position="top-right"
        toastOptions={{ className: "!rounded-none !bg-[#121214] !border-white/10 !text-white" }}
      />
      <BrowserRouter>
        <DeepLinkListener />
        <ImpersonationBanner />
        <TrialEndingToast />
        <InstallPrompt />
        <AutoTrackerBootstrap />
        <FcmBootstrap />
        <RevenueCatBootstrap />
        <RootRouter />
      </BrowserRouter>
    </div>
  );
}

function App() {
  useEffect(() => {
    initNativeShell();
    initIap();
  }, []);

  return (
    <AuthProvider>
      <AppContent />
    </AuthProvider>
  );
}

/** RevenueCat identity bootstrap (native only).
 *  After login, ties the RevenueCat customer to our backend `user_id` so the
 *  webhook can map purchases (app_user_id == users.user_id). On logout, resets
 *  RevenueCat so the next account on this device doesn't inherit entitlements.
 *  Skipped while an admin is impersonating or in demo mode. */
function RevenueCatBootstrap() {
  const { user } = useAuth();
  const lastId = useRef(null);
  useEffect(() => {
    if (!Capacitor.isNativePlatform()) return;
    const id = user && !user._impersonating && !user._demo ? user.user_id : null;
    if (id && id !== lastId.current) {
      lastId.current = id;
      initRevenueCat(id);
    } else if (!user && lastId.current) {
      lastId.current = null;
      logOutRevenueCat();
    }
  }, [user]);
  return null;
}

/** Global auto-tracker bootstrap — starts GPS service at app boot if the
 *  user has auto_track_enabled, regardless of which page they're on.
 *  This ensures mileage recording works even if the Mileage page is never opened. */
function AutoTrackerBootstrap() {
  const { user } = useAuth();
  useEffect(() => {
    if (!user || user.role !== "owner") return;
    let cancelled = false;
    (async () => {
      try {
        const res = await api.get("/mileage/subscription-status");
        if (cancelled) return;
        if (res.data?.auto_track_enabled) {
          const { startBackgroundTracker } = await import("@/lib/backgroundTracker");
          await startBackgroundTracker();
          console.info("[AutoTrackerBootstrap] Background tracker started at app boot");
        }
      } catch (e) {
        console.warn("[AutoTrackerBootstrap] Failed to auto-start tracker:", e);
      }
    })();
    return () => { cancelled = true; };
  }, [user]);
  return null;
}

// Keep this at the very bottom
function FcmBootstrap() {
  useFcmPush();
  return null;
}

export default App;
